import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import reduce from 'lodash/reduce';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import Checkbox from '../../../common/components/Checkbox';
import Table from '../../../common/components/Table';
import {
  RESOURCE_TYPE__WORKFLOW,
  RESOURCE_TYPE__REPORT,
  RESOURCE_TYPE__PROJECT,
  RESOURCE_TYPE__BLUEPRINT,
  RESOURCE_TYPE__PATIENT,
  RESOURCE_TYPE__QUESTIONNAIRE,
  RESOURCE_TYPE__PATIENT_NOTE,
  RESOURCE_TYPE__DASHBOARD,
  RESOURCE_TYPE__MILESTONE,
  RESOURCE_TYPE__PARTICIPATION,
  RESOURCE_TYPE__ACTIVITY,
  RESOURCE_TYPE__ANSWERS_SHEET,
  RESOURCE_TYPE__AUDIT_LOG,
  RESOURCE_TYPE__USER,
  RESOURCE_TYPE__DOMAIN,
  RESOURCE_TYPE__GROUP,
  RESOURCE_TYPE__ROLE,
  RESOURCE_TYPE__VARIABLE,
  RESOURCE_TYPE__NOTICE,
  RESOURCE_TYPE__EAPP,
  RESOURCE_TYPE__FORM_BUILDER_PROJECT,
  RESOURCE_TYPE__OMOP,
} from '../../../common/permissions/new';
import branding from '../../../utils/branding';

const getLabelForResourceType = (resourceType) => {
  switch (resourceType) {
    case RESOURCE_TYPE__WORKFLOW:
      return 'permissions:resource.workflow';
    case RESOURCE_TYPE__REPORT:
      return 'permissions:resource.report';
    case RESOURCE_TYPE__PROJECT:
      return 'permissions:resource.project';
    case RESOURCE_TYPE__BLUEPRINT:
      return 'permissions:resource.blueprint';
    case RESOURCE_TYPE__PATIENT:
      return 'permissions:resource.patient';
    case RESOURCE_TYPE__QUESTIONNAIRE:
      return 'permissions:resource.questionnaire';
    case RESOURCE_TYPE__PATIENT_NOTE:
      return 'permissions:resource.patientNote';
    case RESOURCE_TYPE__DASHBOARD:
      return 'permissions:resource.dashboard';
    case RESOURCE_TYPE__MILESTONE:
      return 'permissions:resource.milestone';
    case RESOURCE_TYPE__PARTICIPATION:
      return 'permissions:resource.participation';
    case RESOURCE_TYPE__ACTIVITY:
      return 'permissions:resource.activity';
    case RESOURCE_TYPE__ANSWERS_SHEET:
      return 'permissions:resource.answersSheet';
    case RESOURCE_TYPE__AUDIT_LOG:
      return 'permissions:resource.auditLog';
    case RESOURCE_TYPE__USER:
      return 'permissions:resource.user';
    case RESOURCE_TYPE__DOMAIN:
      return 'permissions:resource.domain';
    case RESOURCE_TYPE__GROUP:
      return 'permissions:resource.group';
    case RESOURCE_TYPE__ROLE:
      return 'permissions:resource.role';
    case RESOURCE_TYPE__VARIABLE:
      return 'permissions:resource.variable';
    case RESOURCE_TYPE__NOTICE:
      return 'permissions:resource.notice';
    case RESOURCE_TYPE__EAPP:
      return 'permissions:resource.eapp';
    case RESOURCE_TYPE__FORM_BUILDER_PROJECT:
      return 'permissions:resource.formBuilderProject';
    case RESOURCE_TYPE__OMOP:
      return 'permissions:resource.omop';
    default:
      return '';
  }
};

const renderLabel = (permission, t) => {
  const key = getLabelForResourceType(permission.resourceType);
  return (
    <>
      {key && <span>[{t(key)}]</span>}
      {key && ' '}
      <span>
        {t(permission.label, {
          context: branding,
        })}
      </span>
    </>
  );
};

const PermissionsGroup = ({ role, onChange, options }) => {
  const { t } = useTranslation();
  const labels = uniq(
    map(
      filter(options, {
        showInTable: true,
      }),
      'label',
    ),
  );

  const onOneOffPermissionChange = useCallback(
    (perm, event) => {
      const roleCopy = cloneDeep(role);
      const index = findIndex(roleCopy.permissions, {
        key: perm.value,
      });
      const state = event.target.checked;
      if (index > -1) {
        if (state) {
          roleCopy.permissions[index] = {
            key: perm.value,
          };
        } else {
          roleCopy.permissions.splice(index, 1);
        }
      } else {
        roleCopy.permissions.push({
          key: perm.value,
        });
      }
      onChange(roleCopy);
    },
    [role, onChange],
  );

  const onGeneralPermissionChange = useCallback(
    ({ label, permissions }, event) => {
      const roleCopy = cloneDeep(role);
      const permByLabel = find(permissions, {
        label,
      });
      const index = !permByLabel
        ? -1
        : findIndex(roleCopy.permissions, {
            key: permByLabel.value,
          });
      const state = event.target.checked;
      if (index > -1) {
        if (state) {
          roleCopy.permissions[index] = {
            key: permByLabel.value,
          };
        } else {
          roleCopy.permissions.splice(index, 1);
        }
      } else if (permByLabel) {
        roleCopy.permissions.push({
          key: permByLabel.value,
        });
      }

      onChange(roleCopy);
    },
    [role, onChange],
  );

  const generalPermissions = filter(options, {
    showInTable: true,
  });
  const oneOffPermissions = filter(options, (opt) => !opt.showInTable);

  const mappedOneOffPermissions = oneOffPermissions.map((oop) => {
    const copy = cloneDeep(oop);
    const checked = !!find(role.permissions, {
      key: copy.value,
    });
    copy.checked = checked;
    return copy;
  });

  const groupedGeneralPermissions = reduce(
    generalPermissions,
    (acc, cur) => {
      const index = findIndex(acc, {
        resourceType: cur.resourceType,
      });

      // check if this permission option already
      // exists in this role
      const checked = !!find(role.permissions, {
        key: cur.value,
      });

      if (index >= 0) {
        const found = acc[index];
        found.permissions.push({
          label: cur.label,
          value: cur.value,
          checked,
        });
        acc.splice(index, 1, found);
      } else {
        acc.push({
          key: cur.resourceType,
          resourceType: cur.resourceType,
          permissions: [
            {
              label: cur.label,
              value: cur.value,
              checked,
            },
          ],
        });
      }
      return acc;
    },
    [],
  );

  const columns = [
    {
      dataIndex: 'resourceType',
      key: 'resourceType',
      render: (value) => t(getLabelForResourceType(value)),
    },
    ...labels.map((label) => ({
      title: <p className="text-center">{t(label)}</p>,
      dataIndex: 'permissions',
      key: label,
      render: (_, { permissions, resourceType }) => {
        const columnPermission = find(permissions, {
          label,
        });
        const checked = !!(columnPermission && columnPermission.checked);
        return (
          columnPermission && (
            <div
              className="flex justify-center"
              key={`${resourceType}:${label}`}
            >
              <Checkbox
                checked={checked}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={onGeneralPermissionChange.bind(null, {
                  label,
                  permissions,
                })}
                disabled={!columnPermission}
              />
            </div>
          )
        );
      },
    })),
  ];

  return (
    <div className="stack-6">
      {!isEmpty(groupedGeneralPermissions) && (
        <div className="stack-4">
          <h3 className="text-lg font-medium">
            {t('permissions:generalPermissions')}
          </h3>
          <Table
            dataSource={groupedGeneralPermissions}
            columns={columns}
            pagination={null}
          />
        </div>
      )}
      {!isEmpty(mappedOneOffPermissions) && (
        <div className="stack-4">
          <h3 className="text-lg font-medium">
            {t('permissions:oneOffPermissions')}
          </h3>
          <div className="stack-4">
            {mappedOneOffPermissions.map((oop) => (
              // TODO: Remove extra div when moving checkboxes to block level
              // It can be achieved after moving away from Ant Design
              <div key={oop.value}>
                <Checkbox
                  checked={oop.checked}
                  // eslint-disable-next-line react/jsx-no-bind
                  onChange={onOneOffPermissionChange.bind(null, oop)}
                >
                  {renderLabel(oop, t)}
                </Checkbox>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

PermissionsGroup.propTypes = {
  role: PropTypes.shape({
    permissions: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
      }),
    ),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      showInTable: PropTypes.bool,
      resourceType: PropTypes.string,
    }),
  ),
};

PermissionsGroup.defaultProps = {
  options: [],
};

export default PermissionsGroup;
